import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { IColorOption } from '@opengov/stories-shared/types';
import Color from 'color';
import React, { useEffect, useState } from 'react';

import { colorSelect as msgs } from '../../../../locale/stories.en.locale.json';
import { DEFAULT_COLOR_BAR_LENGTH } from '../../../constants';
import { ColorControlsModal } from '../ColorControlsModal';
import { styles } from './ColorSelect.styles';

export type IColorSelectProps = {
  maxColorBarLength?: number;
  colors: IColorOption[];
  title: string;
  pageBgColorCode?: string;
  onChange(colors: IColorOption[]): void;
};

const DEFAULT_PAGE_BG_COLOR = '#EFF2F5';

export const ColorSelect = (props: IColorSelectProps) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [currentColors, setCurrentColors] = useState(props.colors);

  const {
    title,
    maxColorBarLength = DEFAULT_COLOR_BAR_LENGTH,
    pageBgColorCode = DEFAULT_PAGE_BG_COLOR,
    onChange
  } = props;

  useEffect(() => {
    setCurrentColors(props.colors);
  }, [props.colors]);

  const handleCloseColorPicker = () => setShowColorPicker(false);

  const handleChange = (newColors: IColorOption[]) => {
    setCurrentColors(newColors);
    onChange(newColors);
  };

  const handleColorAdded = (colorCode: string) => {
    const updatedColors = [...currentColors, { colorCode, selected: false }];
    handleCloseColorPicker();
    handleChange(updatedColors);
  };

  const validateColor = (colorCode: string) => {
    const existingColor = currentColors.find((item) => Color(item.colorCode).contrast(Color(colorCode)) === 1);
    if (existingColor) return msgs.exist;
    return null;
  };

  const handleRemoveColor = (colorCode: string) => {
    handleChange(currentColors.filter((item) => item.colorCode !== colorCode));
  };

  const handleColorSelect = (colorCode: string) => {
    handleChange(currentColors.map((colorOption) => ({
      colorCode: colorOption.colorCode,
      selected: colorOption.colorCode === colorCode
    })));
  };

  const isMaxColorAdded = currentColors.length >= maxColorBarLength;

  const addButton = (
    <Button
      sx={styles.addButton}
      onClick={() => setShowColorPicker(true)}
      disabled={isMaxColorAdded}
      aria-label="Add color"
      data-test="add-color"
    >
      <AddIcon fontSize="small" />
    </Button>
  );

  return (
    <Box sx={styles.container} data-test="color-select">
      <Typography variant="caption" data-test="color-select-label" fontWeight={500}>
        {title} {!!maxColorBarLength && `(${msgs.max} ${maxColorBarLength})`}
      </Typography>
      <Box sx={styles.colorsRow}>
        {currentColors.map(({ colorCode, selected }) => {
          const contrastRatio = Color(pageBgColorCode).contrast(Color(colorCode));
          const hasBorder = contrastRatio < 1.2;
          return (
            <Box key={colorCode} sx={styles.colorOption} data-test="color-swatch-item">
              <Button
                sx={hasBorder ? styles.colorButtonWithBorder : styles.colorButton}
                style={{ backgroundColor: colorCode }}
                onClick={() => handleColorSelect(colorCode)}
                aria-label={`Select color ${colorCode}`}
                data-test="color-swatch"
              />
              {selected ? (
                <Box sx={styles.colorBadge} data-test="selected-color-badge">
                  <CheckCircleIcon sx={styles.selectedIcon} />
                </Box>
              ) : (
                <Box
                  className="color-remove-badge"
                  sx={styles.removeBadge}
                  role="button"
                  aria-label={`Remove color ${colorCode}`}
                  onClick={() => handleRemoveColor(colorCode)}
                  data-test="color-remove"
                >
                  <CloseIcon sx={styles.removeIcon} />
                </Box>
              )}
            </Box>
          );
        })}

        {isMaxColorAdded ? (
          <Tooltip title={msgs.maxReached.replace('{maxCount}', maxColorBarLength.toString())}>
            <span>
              {addButton}
            </span>
          </Tooltip>
        ) : (
          addButton
        )}
      </Box>

      {showColorPicker && (
        <ColorControlsModal
          colorValidation={validateColor}
          onSave={handleColorAdded}
          onClose={handleCloseColorPicker}
        />
      )}
    </Box>
  );
};
